/**
 * Keeps pages of a spec-driven prototype on its data layer (`src/lib/appStore.ts`), on the
 * streaming path where the browser executes writeFile. A copy of chef's
 * `app/lib/.server/prototyper/appDataLayer.ts` (from `sharedEntitiesFromSamples` down); keep
 * the two identical, like `llm-content-fixes.ts` and chef's `auto-import.ts`.
 */

export const APP_STORE_PATH = 'src/lib/appStore.ts';
export const STARTUP_SAMPLES_PATH = 'src/data/startupSamples.ts';

/** Same rule as the AI API's `entity_key` and the layer's own: `"sales order"` → `"SalesOrder"`. */
export function entityKey(name: string): string {
  const key = name
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
  return key.replace(/^[^A-Za-z]+/, '').slice(0, 64);
}

/**
 * The shared entities in `src/data/startupSamples.ts` that have records (its non-empty keys).
 *
 * An empty list is not shared data: the AI API sends every entity another prototype registered,
 * records or not, and dropping a page's sample rows for `"Class": []` left every screen of a
 * prototype on "No classes found" (27 Sep 2026). With the seed kept, the layer's first load
 * writes those rows into the startup's store, which is still empty.
 */
export function sharedEntitiesFromSamples(source: string | undefined): Set<string> {
  const out = new Set<string>();
  for (const m of (source ?? '').matchAll(/^\s*"([A-Za-z][A-Za-z0-9_]*)"\s*:\s*\[(?!\s*\])/gm)) out.add(m[1]);
  return out;
}

const DATA_IMPORT = /import\s*\{([^}]*)\}\s*from\s*(['"])([^'"]*(?:\/data\/|mockData)[^'"]*)\2\s*;?/g;
const IDENT = /^[A-Za-z_$][\w$]*$/;

/** `initialOrders` → `Order`: the entity a sample array most likely holds. */
function entityFromArrayName(name: string): string | null {
  let base = name.replace(/^(mock|initial|sample|seed|default|all)(?=[A-Z])/, '');
  base = base.charAt(0).toUpperCase() + base.slice(1);
  if (/ies$/.test(base)) base = base.slice(0, -3) + 'y';
  else if (/s$/.test(base) && !/(ss|us|is)$/.test(base)) base = base.slice(0, -1);
  return /^[A-Z][A-Za-z0-9]*$/.test(base) ? base : null;
}

function countWord(content: string, word: string): number {
  return content.match(new RegExp(`(?<![\\w$.])${word.replace(/\$/g, '\\$')}(?![\\w$])`, 'g'))?.length ?? 0;
}

/**
 * Lists seeded from a sample array go through `useEntity`, and an entity the startup already has
 * takes no seed. Imports left unused by either change are removed, so a strict build still
 * passes; the auto-import adds `useEntity`.
 */
export function applyAppDataConventions(
  content: string,
  shared: ReadonlySet<string>,
): { content: string; changed: string[] } {
  const changed: string[] = [];
  const dataNames = new Set<string>();
  for (const m of content.matchAll(DATA_IMPORT)) {
    for (const part of m[1].split(',')) {
      const local = part.trim().split(/\s+as\s+/).pop()?.replace(/^type\s+/, '').trim();
      if (local && IDENT.test(local)) dataNames.add(local);
    }
  }

  let out = content.replace(
    /\buseState\s*(?:<\s*([A-Z][A-Za-z0-9_]*)\s*\[\]\s*>)?\s*\(\s*([A-Za-z_$][\w$]*)\s*\)/g,
    (whole, typeName: string | undefined, ident: string) => {
      if (!dataNames.has(ident)) return whole;
      const entity = typeName ?? entityFromArrayName(ident);
      if (!entity) return whole;
      changed.push(`useState(${ident}) → useEntity('${entity}')`);
      return `useEntity${typeName ? `<${typeName}>` : ''}('${entity}', ${ident})`;
    },
  );

  out = out.replace(
    /\buseEntity\s*(<[^()]*?>)?\s*\(\s*(['"])([A-Za-z][A-Za-z0-9_ ]*)\2\s*,\s*[A-Za-z_$][\w$.]*\s*\)/g,
    (whole, generic: string | undefined, quote: string, name: string) => {
      if (!shared.has(entityKey(name))) return whole;
      changed.push(`dropped the sample seed of shared ${name}`);
      return `useEntity${generic ?? ''}(${quote}${name}${quote})`;
    },
  );

  if (changed.length === 0) return { content, changed };

  // Imports the rewrites left unused: sample arrays, and useState when nothing else uses it.
  out = out.replace(DATA_IMPORT, (whole, names: string, quote: string, source: string) => {
    const kept = names
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .filter((p) => {
        const local = p.split(/\s+as\s+/).pop()?.replace(/^type\s+/, '').trim() ?? '';
        return !dataNames.has(local) || countWord(out, local) > 1;
      });
    if (kept.length === 0) return '';
    return `import { ${kept.join(', ')} } from ${quote}${source}${quote};`;
  });
  if (countWord(out, 'useState') === 1) {
    out = out.replace(/import\s*\{([^}]*)\}\s*from\s*(['"])react\2\s*;?/, (whole, names: string, quote: string) => {
      const kept = names.split(',').map((p) => p.trim()).filter((p) => p && p !== 'useState');
      return kept.length > 0 ? `import { ${kept.join(', ')} } from ${quote}react${quote};` : '';
    });
    out = out.replace(/import\s+(\w+)\s*,\s*\{\s*\}\s*from\s*(['"])react\2/, 'import $1 from $2react$2');
  }
  return { content: out, changed };
}
